import { AxiosResponse } from 'axios';
import { getAccessPoints } from '../api/access-points';
import { getActivities } from '../api/activities';
import { getContracts } from '../api/activities-nim';
import { getDimrVersions, getDimrVersionsWithActivities } from '../api/documents/dimrs';
import { getIs37Sensors } from '../api/documents/is37s';
import { getCryoEquipment } from '../api/documents/lockouts';
import { getPersonsEros, getPersonsHse, getPersonsLR, getPersonsTso } from '../api/documents/vics';
import { getWdpVersions } from '../api/documents/wdps';
import {
    AccessPointReadDTO,
    ActivityReadDTO,
    ApiErrorDTO,
    ContractNimReadDTO,
    CryoEquipmentLocalReadDTO,
    CsamSensorReadDTO,
    DimrVersionGridActivityReadDTO,
    DimrVersionGridReadDTO,
    EgroupWithMembersDTO,
    EquipmentReadDTO,
    InterventionPeriodNimReadDTO,
    ListOfValuesReadDTO,
    LocationDTO,
    OrgUnitReferenceDTO,
    PersonReadDTO,
    SupplierReadDTO,
    SystemBsSrcReadDTO,
    TagReadDTO,
    WdpVersionGridReadDTO,
    WorkOrderReadDTO,
} from '../api/dto';
import { getEgroups } from '../api/egroups';
import { getActiveEquipments, getEquipments } from '../api/equipment';
import { getFacilityInterventionPeriods, getInterventionPeriodNames } from '../api/interventionPeriods';
import { getLocationsByName } from '../api/locations';
import { getMeetings } from '../api/meetings';
import { getOrgUnits } from '../api/orgUnits';
import { getCernEmployees, getFrsOfficers, getPersons, getPersonsForFiltering } from '../api/persons';
import { getSuppliers } from '../api/suppliers';
import { getSystems } from '../api/system';
import { getSelectableTags, getTags } from '../api/tags';
import { Pageable } from '../api/types';
import { getWorkOrders } from '../api/work-orders';
import {
    buildInterventionPeriodDefaultFilter,
    makeAccessPointDefaultFilter,
    makeActiveOrDraftActivityIdFilter,
    makeActivityIdFilter,
    makeInterventionPeriodDefaultFilter,
    makeTagsDefaultFilter,
} from './filters/activity';
import { makeDimrVersionByMasterIdFilter, makeDimrVersionCodeFilter } from './filters/dimr';
import { makeLockoutDefaultFilter } from './filters/lockout';
import { makeOrgUnitFilter, makeOrgUnitIdFilter } from './filters/orgUnit';
import { makePersonIdFilter, makePersonNameFilter } from './filters/person';
import { createSupplierCodeQuery, createSupplierNameQuery } from './filters/supplier';
import { makeSystemFilter } from './filters/system';
import { andClauses, getDependentCode, optionalEqualClause } from './filters/utils';
import {
    makeContractsDefaultFilter,
    makeEgroupDefaultFilter,
    makeMeetingsDefaultFilter,
    makeSensorsDefaultFilter,
    makeWorkOrdersDefaultFilter,
} from './filters/various';
import { makeWdpVersionCodeFilter } from './filters/wdp';

export const fetchCernEmployeesByName = async (
    searchTerm: string,
    exactId: boolean | undefined
): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getCernEmployees({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchPeopleByName = async (searchTerm: string, exactId: boolean | undefined): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getPersons({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchPeopleForFiltering = async (
    searchTerm: string,
    exactId: boolean | undefined
): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getPersonsForFiltering({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchFrsOfficersByName = async (
    searchTerm: string,
    exactId: boolean | undefined
): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getFrsOfficers({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchTsoByName = async (searchTerm: string, exactId: boolean | undefined): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getPersonsTso({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchHseByName = async (searchTerm: string, exactId: boolean | undefined): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getPersonsHse({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchErosByName = async (searchTerm: string, exactId: boolean | undefined): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getPersonsEros({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchLocationResponsibleByName = async (
    searchTerm: string,
    exactId: boolean | undefined
): Promise<PersonReadDTO[]> => {
    const result: Pageable<PersonReadDTO> = await getPersonsLR({
        filter: exactId ? makePersonIdFilter(searchTerm) : makePersonNameFilter(searchTerm),
    });
    return result.content;
};

export const fetchOrgUnits = async (
    searchTerm: string,
    exactId: boolean | undefined,
    idField: string | undefined
): Promise<OrgUnitReferenceDTO[]> => {
    const result: Pageable<OrgUnitReferenceDTO> = await getOrgUnits({
        filter: exactId ? makeOrgUnitIdFilter(searchTerm, idField) : makeOrgUnitFilter(searchTerm),
        sort: ['orgLevel,asc', 'code,asc'],
    });
    return result.content;
};

export const fetchSystems = async ({
    searchTerm,
    facilityCode,
}: {
    searchTerm: string;
    facilityCode: string | undefined;
}): Promise<SystemBsSrcReadDTO[]> => {
    const result: Pageable<SystemBsSrcReadDTO> = await getSystems({
        filter: makeSystemFilter(searchTerm),
        sort: ['bPath,asc'],
        config: { params: { facilityCode } },
    });
    return result.content;
};

export const fetchEquipment = async (searchTerm: string): Promise<EquipmentReadDTO[]> => {
    const equipment: EquipmentReadDTO[] = await getEquipments({
        searchTerm,
    });
    return equipment;
};

export const fetchActiveEquipment = async (searchTerm: string): Promise<EquipmentReadDTO[]> => {
    const equipment: EquipmentReadDTO[] = await getActiveEquipments({
        searchTerm,
    });
    return equipment;
};

export const fetchLocationsByName = async ({
    searchTerm,
    selectable,
    facilityCode,
}: {
    searchTerm: string;
    selectable: boolean;
    facilityCode: string | undefined;
}): Promise<LocationDTO[]> => {
    return await getLocationsByName({
        query: searchTerm,
        onlySelectable: selectable,
        facilityCode,
    });
};
export const fetchSensorsByName = async (searchTerm: string): Promise<CsamSensorReadDTO[]> => {
    const result: Pageable<CsamSensorReadDTO> = await getIs37Sensors({
        filter: makeSensorsDefaultFilter(searchTerm),
    });
    return result.content;
};

export const fetchContractsByString = async (searchTerm: string): Promise<ContractNimReadDTO[]> => {
    const result: Pageable<ContractNimReadDTO> = await getContracts({
        filter: makeContractsDefaultFilter(searchTerm),
    });
    return result.content;
};

export const fetchAccessPointsByName = async (searchTerm: string): Promise<AccessPointReadDTO[]> => {
    const result: Pageable<AccessPointReadDTO> = await getAccessPoints({
        filter: makeAccessPointDefaultFilter(searchTerm),
    });
    return result.content;
};

export const fetchTagsByName = async (searchTerm: string, onlyTaggable: boolean): Promise<TagReadDTO[]> => {
    const filter = makeTagsDefaultFilter(searchTerm);
    const fetchTags = onlyTaggable ? getSelectableTags : getTags;
    const result: Pageable<TagReadDTO> = await fetchTags({ filter });
    return result.content;
};

export const fetchEgroups = async (searchTerm: string): Promise<EgroupWithMembersDTO[]> => {
    const result: Pageable<EgroupWithMembersDTO> = await getEgroups({
        filter: makeEgroupDefaultFilter(searchTerm),
    });
    return result.content;
};

export const fetchMeetings = async (searchTerm: string): Promise<ListOfValuesReadDTO[]> => {
    const result: Pageable<ListOfValuesReadDTO> = await getMeetings({
        filter: makeMeetingsDefaultFilter(searchTerm),
    });
    return result.content;
};

export const fetchActivitiesById = async (searchTerm: string): Promise<ActivityReadDTO[]> => {
    const result: Pageable<ActivityReadDTO> = await getActivities({
        filter: makeActivityIdFilter(searchTerm),
    });
    return result.content;
};

export const fetchActiveOrDraftActivitiesById = async (searchTerm: string): Promise<ActivityReadDTO[]> => {
    const result: Pageable<ActivityReadDTO> = await getActivities({
        filter: makeActiveOrDraftActivityIdFilter(searchTerm),
    });
    return result.content;
};

export const fetchDimrVersionsById = async <B extends boolean>(
    searchTerm: string,
    activities: B,
    onlyDrafts?: boolean
): Promise<B extends true ? DimrVersionGridActivityReadDTO[] : B extends false ? DimrVersionGridReadDTO[] : never> => {
    const result = await (activities ? getDimrVersionsWithActivities : getDimrVersions)({
        filter: makeDimrVersionCodeFilter(searchTerm, !!onlyDrafts),
    });
    return result.content satisfies DimrVersionGridReadDTO[] | DimrVersionGridActivityReadDTO[] as any;
};

export const fetchDimrVersionsByMasterId = async (masterId: number): Promise<DimrVersionGridReadDTO[]> => {
    const result = await getDimrVersions({
        filter: makeDimrVersionByMasterIdFilter(masterId),
    });
    return result.content;
};

export const fetchWdpVersionsById = async (searchTerm: string): Promise<WdpVersionGridReadDTO[]> => {
    const result: Pageable<WdpVersionGridReadDTO> = await getWdpVersions({
        filter: makeWdpVersionCodeFilter(searchTerm),
    });
    return result.content;
};

export const isErrorResponseWithItems = (response: AxiosResponse): response is AxiosResponse<ApiErrorDTO<any>> =>
    !!response?.data?.errorItems;

export const fetchSupplierByName = async (searchTerm: string): Promise<SupplierReadDTO[]> => {
    const result: Pageable<SupplierReadDTO> = await getSuppliers({
        filter: createSupplierNameQuery(searchTerm),
    });
    return result.content;
};

export const fetchSupplierByCode = async (searchTerm: string): Promise<SupplierReadDTO[]> => {
    const result: Pageable<SupplierReadDTO> = await getSuppliers({
        filter: createSupplierCodeQuery(searchTerm),
    });
    return result.content;
};

export const fetchCryoEquipmentByNameOrDescription = async (
    searchTerm: string
): Promise<CryoEquipmentLocalReadDTO[]> => {
    const result: Pageable<CryoEquipmentLocalReadDTO> = await getCryoEquipment({
        filter: makeLockoutDefaultFilter(searchTerm),
    });
    return result.content;
};

export const fetchInterventionPeriodsByFacility = async (
    searchTerm: string,
    exactId?: boolean,
    idField?: string,
    values?: any[]
) =>
    getFacilityInterventionPeriods({
        facilityCode: (values?.[0] as ListOfValuesReadDTO | null | undefined)?.code ?? null,
    });

const fetchInterventionPeriodsByFacilityAndTypeFields = async (
    searchTerm: string,
    values: any[] | undefined,
    facilityField: string,
    periodTypeField: string
): Promise<InterventionPeriodNimReadDTO[]> => {
    const facilityCode = getDependentCode(values, facilityField);
    const periodTypeCode = getDependentCode(values, periodTypeField);

    const filter = andClauses(
        buildInterventionPeriodDefaultFilter(searchTerm),
        optionalEqualClause('interventionFacility.code', facilityCode),
        optionalEqualClause('periodType.code', periodTypeCode)
    );
    const result: Pageable<InterventionPeriodNimReadDTO> = await getInterventionPeriodNames({
        filter,
        sort: ['periodStart,desc', 'periodName,asc'],
    });
    return result.content;
};

export const fetchTaskScheduledInterventionPeriods = async (
    searchTerm: string,
    _exactId?: boolean,
    _idField?: string,
    values?: any[]
): Promise<InterventionPeriodNimReadDTO[]> =>
    fetchInterventionPeriodsByFacilityAndTypeFields(
        searchTerm,
        values,
        'document.facility.code',
        'document.interventionPeriod.periodType.code'
    );

export const fetchTaskProposedInterventionPeriods = async (
    searchTerm: string,
    _exactId?: boolean,
    _idField?: string,
    values?: any[]
): Promise<InterventionPeriodNimReadDTO[]> =>
    fetchInterventionPeriodsByFacilityAndTypeFields(
        searchTerm,
        values,
        'document.facility.code',
        'document.proposedInterventionPeriod.periodType.code'
    );

export const fetchActivityScheduledInterventionPeriods = async (
    searchTerm: string,
    _exactId?: boolean,
    _idField?: string,
    values?: any[]
): Promise<InterventionPeriodNimReadDTO[]> =>
    fetchInterventionPeriodsByFacilityAndTypeFields(
        searchTerm,
        values,
        'facility.code',
        'interventionPeriod.periodType.code'
    );

export const fetchActivityProposedInterventionPeriods = async (
    searchTerm: string,
    _exactId?: boolean,
    _idField?: string,
    values?: any[]
): Promise<InterventionPeriodNimReadDTO[]> =>
    fetchInterventionPeriodsByFacilityAndTypeFields(
        searchTerm,
        values,
        'facility.code',
        'proposedInterventionPeriod.periodType.code'
    );

export const fetchInterventionPeriodNames = async (searchTerm: string): Promise<InterventionPeriodNimReadDTO[]> => {
    const result: Pageable<InterventionPeriodNimReadDTO> = await getInterventionPeriodNames({
        filter: makeInterventionPeriodDefaultFilter(searchTerm),
        sort: ['periodStart,desc', 'periodName,asc'],
    });
    return result.content;
};

export const fetchWorkOrdersById = async (searchTerm: string): Promise<WorkOrderReadDTO[]> => {
    const result: Pageable<WorkOrderReadDTO> = await getWorkOrders({
        filter: makeWorkOrdersDefaultFilter(searchTerm),
    });
    return result.content;
};
